/**
 * Builds Google and Outlook calendar URLs for preview only.
 * Mirrors backend logic in transition-firebase/functions/src/emails/calendarLinks.ts
 * so the preview matches the sent email. Used when we have activity date/time.
 */

function formatGoogleCalendarDateUtc(date: Date): string {
  const pad2 = (n: number) => String(n).padStart(2, '0')
  return (
    String(date.getUTCFullYear()) +
    pad2(date.getUTCMonth() + 1) +
    pad2(date.getUTCDate()) +
    'T' +
    pad2(date.getUTCHours()) +
    pad2(date.getUTCMinutes()) +
    pad2(date.getUTCSeconds()) +
    'Z'
  )
}

function buildGoogleCalendarUrl({
  title,
  description,
  start,
  end
}: {
  title: string
  description?: string
  start: Date
  end: Date
}): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatGoogleCalendarDateUtc(start)}/${formatGoogleCalendarDateUtc(end)}`
  })
  if (description) {
    params.set('details', description)
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

function formatOutlookDateTime(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

function buildOutlookCalendarUrl({
  title,
  description,
  start,
  end
}: {
  title: string
  description?: string
  start: Date
  end: Date
}): string {
  const baseUrl = 'https://outlook.office.com/calendar/0/deeplink/compose'
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    startdt: formatOutlookDateTime(start),
    enddt: formatOutlookDateTime(end)
  })
  if (description) {
    params.set('body', description)
  }
  return `${baseUrl}?${params.toString()}`
}

export interface ActivityForCalendarUrls {
  title: string
  description?: string
  scheduledDate?: string
  scheduledTime?: string
  duration: number
}

/**
 * Build start/end from activity scheduledDate, scheduledTime and duration.
 * scheduledDate: ISO date string (e.g. "2025-02-20"), scheduledTime: "14:00".
 */
function getStartEnd(activity: ActivityForCalendarUrls): { start: Date; end: Date } | null {
  const { scheduledDate, scheduledTime, duration } = activity
  if (!scheduledDate || duration == null) return null
  const timePart = scheduledTime && /^\d{1,2}:\d{2}$/.test(scheduledTime) ? scheduledTime : '09:00'
  const start = new Date(`${scheduledDate}T${timePart}:00`)
  if (Number.isNaN(start.getTime())) return null
  const end = new Date(start.getTime() + duration * 60 * 1000)
  return { start, end }
}

export function buildCalendarUrlsForActivity(activity: ActivityForCalendarUrls): {
  googleCalendarUrl: string
  outlookCalendarUrl: string
} {
  const range = getStartEnd(activity)
  const title = activity.title || 'Intraction Activity'
  const description = activity.description

  if (!range) {
    return {
      googleCalendarUrl: '#google-cal',
      outlookCalendarUrl: '#outlook-cal'
    }
  }

  return {
    googleCalendarUrl: buildGoogleCalendarUrl({
      title,
      description,
      start: range.start,
      end: range.end
    }),
    outlookCalendarUrl: buildOutlookCalendarUrl({
      title,
      description,
      start: range.start,
      end: range.end
    })
  }
}
